<template>
    <v-row class="align-center" no-gutters :class="{'flex-grow-0': !isEditMode}">
        <div class="font-weight-regular text-caption me-1">{{ $t('filterTitles.classifications') }}:</div>
        <div v-if="!isEditMode">
            <v-progress-circular v-if="loading" indeterminate size="24" color="grey" />
            <div v-else class="d-flex align-center">
                <span class="me-1">
                    <span v-if="currentClassifications" class="text-subtitle-2 font-weight-regular">
                        {{ currentClassifications }}
                    </span>
                </span>
                <v-btn
                    v-if="!authStore.isAccountant"
                    color="grey"
                    size="small"
                    variant="text"
                    icon="mdi-pencil"
                    data-cy="edit"
                    @click="toggleMode"
                />
            </div>
        </div>
        <div v-else class="d-flex flex-grow-1 align-center">
            <v-select
                v-model="selectedClassifications"
                :items="classifications"
                :placeholder="$t('filterTitles.classifications')"
                clearable
                multiple
                item-title="name"
                item-value="type"
                density="compact"
                color="primary"
                data-cy="classifications"
                hide-details
                max-width="350"
            >
                <template v-slot:chip="{item, props}">
                    <v-chip size="small" v-bind="props" closable>
                        {{ item.title }}
                    </v-chip>
                </template>
            </v-select>
            <v-btn
                :loading="loading"
                icon="mdi-check-circle-outline"
                variant="text"
                color="success"
                class="ms-1 cursor-pointer"
                data-cy="send"
                @click="send"
            />
        </div>
    </v-row>
</template>

<script setup lang="ts">
    import type {SelectOptionType} from '~/utils/types/types'

    const order = ref()
    const loading = ref<boolean>(false)
    const isEditMode = ref<boolean>(false)
    const classifications = ref<SelectOptionType[]>([])
    const selectedClassifications = ref([])
    const currentClassifications = ref()

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        order.value = runtimeStore.orderDetails
        selectedClassifications.value = order.value?.classifications
        await loadData()
        currentClassifications.value = getTranslatedNames()
    })

    function getTranslatedNames() {
        const typeDict = classifications.value.reduce((acc, item) => {
            acc[item.type] = item.name
            return acc
        }, {})
        const translatedNames = selectedClassifications.value.map(type => typeDict[type])
        return translatedNames.join(', ')
    }

    async function loadData() {
        try {
            loading.value = true
            const {classificationTypes} = await app.$api.orderClassificationTypes.get()
            classifications.value = classificationTypes.sort((a, b) => a.name.localeCompare(b.name))
        } catch (e) {
            classifications.value = []
        } finally {
            loading.value = false
        }
    }

    const send = async (): Promise<void> => {
        try {
            loading.value = true
            const data = {classificationsTypes: selectedClassifications.value}
            await app.$api.orders.put(order.value.orderId, data)
            runtimeStore.SET_ORDER_DETAILS({...order.value, ...data})
            order.value = runtimeStore.orderDetails

            currentClassifications.value = getTranslatedNames()
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('data_update_fail'))
        } finally {
            loading.value = false
            toggleMode()
        }
    }

    function toggleMode(): void {
        isEditMode.value = !isEditMode.value
    }
</script>
